import {
	EXTRA_CREDITS_TIERS,
	ExtraCreditsTier,
	ExtraCreditsTierCode,
	IChangeExtraCreditsResponse,
	ICreateCheckoutSessionResponse,
	ICurrentPlanResponse,
	IExtraCreditsChargePreviewResponse,
	IFetchExtraCreditsResponse,
	IFetchPlansResponse,
	IGetCustomerPortalLinkResponse,
	ISExtraCredits,
	ISChangeExtraCreditsResponse,
	ISCreateCheckoutSessionResponse,
	ISCurrentPlanResponse,
	ISExtraCreditsChargePreviewResponse,
	ISFetchExtraCreditsResponse,
	ISFetchPlansResponse,
	ISUpgradePlanResponse,
	ISVerifyPaymentResponse,
	IUpgradePlanResponse,
	IVerifyPaymentResponse,
} from '@/shared/services/payment/payment.types';

export function getStructuredCreateCheckoutSessionResponse(obj: ICreateCheckoutSessionResponse) {
	return {
		sessionId: obj.session_id,
	} as ISCreateCheckoutSessionResponse;
}

export function getStructuredVerifyPaymentResponse(obj: IVerifyPaymentResponse): ISVerifyPaymentResponse {
	return {
		status: obj.status,
		planType: obj.plan_type,
	};
}

export function getStructuredCurrentPlanResponse(obj: ICurrentPlanResponse): ISCurrentPlanResponse {
	return {
		planType: obj.plan_type,
		isOverdue: obj.is_overdue,
		hasPaymentHistory: obj.has_payment_history,
		usage: obj.usage
			? {
					used: obj.usage.used,
					limit: obj.usage.limit,
					remaining: obj.usage.remaining,
					resetAt: obj.usage.reset_at,
					breakdown: obj.usage.breakdown,
				}
			: undefined,
		// Tiers cross the boundary through the parser here too — `add_on_tier` is not
		// guaranteed to already be a value this build knows.
		extraCreditsTier: parseExtraCreditsTier(obj.add_on_tier),
		pendingExtraCreditsTier: parseExtraCreditsTier(obj.pending_add_on_tier),
		// Left in its wire form; `formatEffectiveDate` handles seconds and ISO alike.
		pendingExtraCreditsEffectiveAt: obj.pending_add_on_effective_at ?? null,
	};
}

/**
 * Normalises a tier to its dollar number. The API sends plain numbers (`50`), but has
 * also shipped `"USD_50"` codes, so both are accepted. Returns `null` for anything
 * unrecognised so one unknown tier cannot fail the whole catalog.
 */
function parseExtraCreditsTier(tier: ExtraCreditsTierCode | number | null | undefined): ExtraCreditsTier | null {
	if (tier === null || tier === undefined) return null;

	const dollars = typeof tier === 'number' ? tier : Number(tier.split('_')[1]);
	return EXTRA_CREDITS_TIERS.includes(dollars as ExtraCreditsTier) ? (dollars as ExtraCreditsTier) : null;
}

export function getStructuredFetchExtraCreditsResponse(
	obj: IFetchExtraCreditsResponse
): ISFetchExtraCreditsResponse {
	const recommended = new Set(
		(obj.recommended_tiers ?? []).map(parseExtraCreditsTier).filter((tier) => tier !== null)
	);

	return {
		canPurchase: obj.can_purchase ?? false,
		currency: obj.currency,
		currentExtraCreditsTier: parseExtraCreditsTier(obj.current_add_on_tier),
		pendingExtraCreditsTier: parseExtraCreditsTier(obj.pending_add_on_tier),
		extraCredits: (obj.add_ons ?? [])
			.map((extraCredits) => {
				const extraCreditsTier = parseExtraCreditsTier(extraCredits.tier);
				if (extraCreditsTier === null) return null;

				return {
					extraCreditsTier,
					// The API has shipped payloads without this field; the tier's dollar
					// value IS its credits top-up, so it is the correct fallback.
					monthlyCredits: extraCredits.monthly_allowance_dollars ?? extraCreditsTier,
					recommended: recommended.has(extraCreditsTier),
				};
			})
			.filter((extraCredits): extraCredits is ISExtraCredits => extraCredits !== null),
	};
}

export function getStructuredChangeExtraCreditsResponse(
	obj: IChangeExtraCreditsResponse
): ISChangeExtraCreditsResponse {
	return {
		status: obj.status,
		currentExtraCreditsTier: parseExtraCreditsTier(obj.current_add_on_tier),
		pendingExtraCreditsTier: parseExtraCreditsTier(obj.pending_add_on_tier),
		pendingEffectiveAt:
			obj.effective_at === null || obj.effective_at === undefined
				? null
				: new Date(obj.effective_at * 1000).toISOString(),
		paymentUrl: obj.payment_url ?? null,
	};
}

export function getStructuredExtraCreditsChargePreviewResponse(
	obj: IExtraCreditsChargePreviewResponse
): ISExtraCreditsChargePreviewResponse {
	return {
		amountDue: obj.amount_due_cents / 100,
		monthlyDifference: obj.monthly_difference_cents / 100,
		monthsCharged: obj.months_charged,
		billingInterval: obj.billing_interval,
		renewalAt:
			obj.renewal_at === null || obj.renewal_at === undefined
				? null
				: new Date(obj.renewal_at * 1000).toISOString(),
	};
}

export function getStructuredGetCustomerPortalLinkResponse(obj: IGetCustomerPortalLinkResponse) {
	return {
		url: obj.url,
	} as IGetCustomerPortalLinkResponse;
}

export function getStructuredFetchPlansResponse(obj: IFetchPlansResponse): ISFetchPlansResponse {
	return {
		region: obj.region,
		country: obj.country,
		currency: obj.currency,
		plans: obj.plans.map((p) => ({
			planType: p.plan_type,
			stripePriceId: p.stripe_price_id,
			amount: p.amount,
			interval: p.interval,
			displayLabel: p.display_label,
		})),
	};
}

export function getStructuredUpgradePlanResponse(obj: IUpgradePlanResponse): ISUpgradePlanResponse {
	if (obj.success) {
		return {
			success: true,
			message: obj.message,
			newPlanType: obj.new_plan_type,
		};
	}
	return {
		success: false,
		message: obj.message,
		paymentUrl: obj.payment_url,
	};
}
